import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { CTEyeClientResponse, ClientRegistrationStatus } from '../../../../data/entities';
import CTEyeService from '../../../../services/CTEyeService';
import { showErrorMessage, showSuccessMessage } from '../../../../app/tools';
import { SlideOutModal, Icon, IconDefinitions, InfoDetail, InfoSection } from '../../../../components';
import sharedProperty from '../../../../app/sharedProperty';

interface DisableClientModalProps {
  showState: sharedProperty<boolean>;
  client: CTEyeClientResponse | null;
  onSuccess: () => void;
}

export function DisableClientModal({ showState, client, onSuccess }: DisableClientModalProps) {
  const [loading, setLoading] = useState(false);

  if (!client) return null;

  const isCurrentlyDisabled = client.registrationStatus === ClientRegistrationStatus.Disabled;
  const action = isCurrentlyDisabled ? 'enable' : 'disable';
  const actionTitle = isCurrentlyDisabled ? 'Enable' : 'Disable';

  const handleConfirm = async () => {
    setLoading(true);
    try {
      if (isCurrentlyDisabled) {
        await new CTEyeService().enableClient(client.id);
        showSuccessMessage('Client enabled successfully');
      } else {
        await new CTEyeService().disableClient(client.id);
        showSuccessMessage('Client disabled successfully');
      }
      showState.setter(false);
      onSuccess();
    } catch (error) {
      console.error(`Error ${action}ing client:`, error);
      showErrorMessage(`Failed to ${action} client`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    showState.setter(false);
  };

  const clientDetails: InfoDetail[] = [
    {
      label: "Machine",
      value: client.description.split(' - ')[0],
      icon: <Icon iconName={IconDefinitions.desktop} />,
    },
    {
      label: "User",
      value: client.description.split(' - ')[1] || '—',
      icon: <Icon iconName={IconDefinitions.user} />,
    },
    {
      label: "Company",
      value: client.companyName || 'None',
      icon: <Icon iconName={IconDefinitions.company} />,
    },
    {
      label: "Version",
      value: <code>{client.runningVersion}</code>,
      icon: <Icon iconName={IconDefinitions.code} />,
    },
  ];

  return (
    <SlideOutModal
      showState={showState}
      title={`${actionTitle} Client`}
      footer={
        <div className="row m-0 w-100">
          <div className="col-6 ps-0">
            <Button
              onClick={handleCancel}
              variant="cancel"
              className="w-100"
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
          <div className="col-6 pe-0">
            <Button
              onClick={handleConfirm}
              variant={isCurrentlyDisabled ? "success" : "danger"}
              className="w-100"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  {actionTitle}ing...
                </>
              ) : (
                `${actionTitle} Client`
              )}
            </Button>
          </div>
        </div>
      }
    >
      {/* Client information */}
      <InfoSection title="Client Information" details={clientDetails} />

      {/* Warning/Info alert */}
      <div className={`alert ${isCurrentlyDisabled ? 'alert-info' : 'alert-warning'} py-2 mb-3`}>
        <small className="d-flex">
          <span className="me-2 flex-shrink-0">
            <Icon iconName={isCurrentlyDisabled ? IconDefinitions.circleInfo : IconDefinitions.triangleExclamation} />
          </span>
          <span>
            <strong>{isCurrentlyDisabled ? 'Enabling Client' : 'Disabling Client'}:</strong>{' '}
            {isCurrentlyDisabled
              ? 'This will allow the client to send heartbeats and receive commands again.'
              : 'This will prevent the client from sending heartbeats and receiving commands. The client will remain in your system but will be blocked from communicating with the portal.'}
          </span>
        </small>
      </div>

      {!isCurrentlyDisabled && (
        <div className="alert alert-secondary py-2 mb-0">
          <small className="d-flex">
            <span className="me-2 flex-shrink-0">
              <Icon iconName={IconDefinitions.circleInfo} />
            </span>
            <span>
              <strong>Note:</strong> You can re-enable this client at any time. No data will be lost.
            </span>
          </small>
        </div>
      )}
    </SlideOutModal>
  );
}

export default DisableClientModal;